import React, { useState } from 'react';
import styled from 'styled-components/macro';
// eslint-disable-next-line no-restricted-imports
import { css } from 'styled-components';
import { Cabinet } from 'lib/modelBuilder/entities/Cabinet';
import star from 'assets/images/corners/star.png';
import { FiCopy, FiEdit2, FiX, FiXCircle } from 'react-icons/fi';
import { TiArrowUnsorted } from 'react-icons/ti';
import {
  removeCabinetAction,
  selectCabinetFromIdAction,
  updateCabinetSuccess,
} from 'containers/Configurator/actions/furniture';
import { useDispatch } from 'react-redux';
import { Furniture } from 'lib/modelBuilder/entities/Furniture';
import DimensionsItem from 'containers/FurniturePanel/FurnitureItem/DimensionsItem';
import { useTranslation } from 'react-i18next';
import properties from '../../../helpers/config';

interface Props {
  cabinet: Cabinet;
  furniture: Furniture;
  dragMode: boolean;
  short?: boolean;
  isDragging: boolean;
  removeCabinet: any;
}

const FurnitureItem = ({
  cabinet,
  furniture,
  dragMode,
  short,
  isDragging,
  removeCabinet,
}: Props) => {
  const dispatch = useDispatch();
  const [showAction, displayAction] = useState(false);
  const { t } = useTranslation();

  // useEffect(() => ReactTooltip.rebuild(), [short]);

  let image = cabinet.thumbnail;
  if (!image) {
    // Try to load the image from backend if it's exist
    image = cabinet.image ?? star;
  }

  return (
    <StyledItem
      // className="flex flex-col mx-2 bg-white shadow my-1 -mt-1 w-16"
      onMouseEnter={() => {
        if (!dragMode && short) displayAction(true);
      }}
      onMouseLeave={() => {
        if (short) displayAction(false);
      }}
      short={short}
    >
      {!short && (
        <button
          className="absolute left-0 top-0 -mt-2 -ml-2 text-gray-600 hover:text-red-dark"
          onClick={() => {
            removeCabinet(cabinet);
          }}
          data-tip={t('Remove this cabinet')}
          data-for="action-furniture"
        >
          <div className="bg-white rounded-full m-1">
            <FiXCircle className="h-6 w-6 " />
          </div>
        </button>
      )}
      <div className="flex flex-col">
        <button
          onClick={() => {
            dispatch(selectCabinetFromIdAction(cabinet.id));
          }}
        >
          <StyledImage src={image} short={short} />
        </button>

        {!short && (
          <div className="flex">
            <StyledQuickAction
              onClick={() => {
                dispatch(selectCabinetFromIdAction(cabinet.id));
              }}
              data-tip={t('Edit')}
              data-for="action-furniture"
            >
              <FiEdit2 className="h-6 w-6 " />
            </StyledQuickAction>

            <StyledQuickAction
              onClick={() => {
                dispatch(
                  updateCabinetSuccess(
                    furniture.clone().add(cabinet.makeCopy()),
                    null,
                  ),
                );
              }}
              data-tip={t('Duplicate')}
              data-for="action-furniture"
            >
              <FiCopy className="h-6 w-6" />
            </StyledQuickAction>
          </div>
        )}
      </div>

      <StyledInfo
        data-tip={!short ? t('Drag to modify the order') : null}
        data-for="action-furniture"
      >
        <div
          className="flex justify-center m-1 h-6 flex-grow-0 flex-shrink-0"
          data-tip={short ? t('Drag to modify the order') : null}
          data-for="action-furniture"
        >
          <StyledPrice>
            {cabinet.displayPriceTaxInclude(
              properties.vat,
              properties.resellerRate,
              properties.decimalSeparator,
              properties.thousenSeparator,
            )}
            &nbsp;€
          </StyledPrice>
        </div>
        <div className="border-b mb-1" />
        {showAction && short ? (
          <div className="flex justify-center items-center h-12">
            <StyledActionButton
              onClick={() => {
                dispatch(
                  updateCabinetSuccess(
                    furniture.clone().add(cabinet.makeCopy()),
                    null,
                  ),
                );
              }}
            >
              <FiCopy className="h-6 w-6 text-gray-700" />
            </StyledActionButton>
            <StyledActionButton
              onClick={() => {
                // TODO Add confirmation modal
                dispatch(removeCabinetAction(cabinet.id));
              }}
            >
              <FiX className="h-6 w-6 text-gray-700" />
            </StyledActionButton>
          </div>
        ) : (
          <div className="w-full flex justify-center">
            <DimensionsItem cabinet={cabinet} />
          </div>
        )}
        {!short && (
          <div className="flex items-center justify-center border-t">
            <StyledRotate>
              <TiArrowUnsorted className="h-8 w-8 text-gray-600" />
            </StyledRotate>
          </div>
        )}
      </StyledInfo>

      {/*{!isDragging && <ReactTooltip id="action-furniture" place="bottom" />}*/}
    </StyledItem>
  );
};

export default FurnitureItem;

const StyledItem = styled.div<{ short?: boolean }>`
  margin: 0.25rem 0.25rem 0.5rem 0.25rem;
  background-color: #ffffff;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);

  width: 10rem;
  display: flex;
  position: relative;

  flex-direction: row;
  ${p =>
    p.short &&
    css`
      width: 4rem;
      flex-direction: column;
      margin-top: -0.25rem;
    `}
`;

const StyledPrice = styled.div`
  font-size: 1.125rem;
`;

const StyledActionButton = styled.button`
  width: 50%;
  display: flex;
  justify-content: center;
  align-content: center;
`;

const StyledImage = styled.img<{ short?: boolean }>`
  width: 10rem;
  border-width: 1px;

  ${p =>
    p.short &&
    css`
      //height: 4rem;
      width: 4rem;
    `}
`;

const StyledQuickAction = styled.button`
  margin: 0.25rem 0.75rem;
  color: ${p => p.theme.gray600};

  &:hover {
    color: ${p => p.theme.ternary};
  }
`;

const StyledInfo = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const StyledRotate = styled.div`
  transform: rotate(90deg);
`;
